/**
 * A card for one place data comes from or goes to — "EMAIL · AMAZON SES /
 * Acme Retail / via your own account" with its status in the corner. The
 * concept's `.bp-source`, and the skin the Integrations tab rail wears.
 *
 * ## THE STATUS IS PASSED IN, NEVER COMPUTED HERE
 *
 * `status` is a slot, and it has to stay one. Each integration's panel already
 * decides what its status is from its own readiness contract — SES from the
 * account and identity checks, WhatsApp from the phone number's quality, Meta
 * ads from the ad account read — and those rules took several slices to get
 * honest. A card that derived "Connected" from a boolean would be a second,
 * simpler opinion about the same fact, and it would be the one on screen. So
 * the card draws whatever Badge or StateChip the caller hands it, and nothing
 * here knows what "connected" means.
 *
 * ## THREE ELEMENTS, ONE SKIN
 *
 * The card is `Card`'s `interactive` surface (`CARD_SURFACE` is read from
 * Card, so the two cannot drift) on whichever element the use needs:
 *
 *   - `href` → an `<a>`: the card navigates.
 *   - `onPress` → a `<button>`, with `aria-pressed` for a toggle. Given
 *     `role="tab"` or `role="option"` (and the caller's `aria-controls`), the
 *     state is `aria-selected` instead — the attribute follows the role,
 *     because `aria-selected` on a plain button is not supported by the ARIA
 *     spec and the first build let a caller ask for exactly that (slice 66
 *     review). The rail's tablist owns the keyboard; this card only draws the
 *     state.
 *   - neither → a `<div>`, a static card.
 *
 * `emphasis` (connected, or the one card that matters) fills the icon tile
 * with the brand sweep and warms the border; `pressed` adds a violet wash and
 * a ring. Neither is `bg-brand-gradient`, which suites count as a primary
 * action, and the tile's resting text is `brand-ink`, not `brand-b`, because
 * the tile is a brand tint where brand-b drops to 3.4:1.
 *
 * ## THE HOLE IT LEAVES
 *
 * The kind kicker is free text ("EMAIL · AMAZON SES"). Nothing ties it to a
 * channel enum, so two screens can spell the same provider two ways; the
 * Integrations page is the one caller today and owns the spelling.
 */
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';
import { CARD_SURFACE } from './Card';
import { Kicker } from './Kicker';

export interface SourceCardProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  /** A small glyph for the source, drawn in the tile. */
  icon: ReactNode;
  /** The mono kicker — "EMAIL · AMAZON SES". */
  kind: string;
  name: ReactNode;
  /** A muted line under the name — "via your own account". */
  via?: ReactNode;
  /** The caller's own Badge or StateChip. Never derived here. */
  status: ReactNode;
  /** Brand-filled tile and warmer border. */
  emphasis?: boolean;
  /** Renders the card as a link. */
  href?: string;
  /** Renders the card as a button. */
  onPress?: () => void;
  /** Selected state of a pressable card: `aria-pressed`, or `aria-selected` under `role="tab"`/`"option"`. */
  pressed?: boolean;
}

/** The roles whose selection ARIA spells `aria-selected`. */
const SELECTABLE_ROLES: ReadonlySet<string> = new Set(['tab', 'option']);

export function SourceCard({
  icon,
  kind,
  name,
  via,
  status,
  emphasis = false,
  href,
  onPress,
  pressed,
  className,
  ...rest
}: SourceCardProps) {
  const skin = cn(
    CARD_SURFACE.interactive,
    'flex min-w-0 flex-col gap-1.5 p-4 text-left text-text',
    emphasis && 'border-brand-b/45',
    pressed === true && 'border-brand-b bg-brand-a/16 ring-[3px] ring-brand-b/16',
    className,
  );

  const body = (
    <>
      <span className="flex items-start justify-between gap-2.5">
        <span
          aria-hidden="true"
          className={cn(
            "grid size-[30px] shrink-0 place-items-center rounded-sm [&_svg:not([class*='size-'])]:size-4",
            emphasis ? 'bg-brand-sweep text-white' : 'bg-brand-a/16 text-brand-ink',
          )}
        >
          {icon}
        </span>
        <span className="min-w-0 shrink">{status}</span>
      </span>
      <Kicker as="span" tone="brand" className="mt-2">
        {kind}
      </Kicker>
      <span className="block break-words text-[0.95rem] font-semibold">{name}</span>
      {via !== undefined && via !== null && <span className="block text-[0.78rem] text-muted">{via}</span>}
    </>
  );

  if (href !== undefined) {
    return (
      <a href={href} className={cn(skin, focusRing)} {...rest}>
        {body}
      </a>
    );
  }

  if (onPress !== undefined) {
    const attribute = rest.role !== undefined && SELECTABLE_ROLES.has(rest.role) ? 'aria-selected' : 'aria-pressed';
    const selection = pressed === undefined ? {} : { [attribute]: pressed };
    return (
      <button type="button" onClick={onPress} className={cn(skin, 'w-full cursor-pointer', focusRing)} {...selection} {...rest}>
        {body}
      </button>
    );
  }

  return (
    <div className={skin} {...rest}>
      {body}
    </div>
  );
}
